import React, { useEffect, useState } from "react";
import {
  Modal,
  Button,
  Loader,
  Message,
  Header,
  Icon,
  Segment,
  Statistic,
  Input,
  Form,
} from "semantic-ui-react";
import { AdminUsersV2 } from "../../../../api";
import { useAuth } from "../../../../hooks";

const usersV2Api = new AdminUsersV2();

const MODES = [
  {
    key: "deactivate",
    title: "Desactivar",
    color: "blue",
    description:
      "Cambia el flag active a false. Reversible. El usuario no podra entrar pero todos sus datos se conservan.",
    needsConfirm: false,
  },
  {
    key: "user-only",
    title: "Borrar solo User",
    color: "orange",
    description:
      "Borra el documento User. Los populados (teacher, therapist, paciente, etc.) quedan como datos historicos sin acceso a panel.",
    needsConfirm: true,
  },
  {
    key: "full",
    title: "Cascade completo",
    color: "red",
    description:
      "Borra User + populados + desvincula refs en colecciones cruzadas. Operacion irreversible.",
    needsConfirm: true,
  },
];

function ScopeSummary({ scope, modeKey }) {
  if (!scope || !scope[modeKey]) return null;
  const m = scope[modeKey];
  const docs = m.documents || {};
  return (
    <Segment>
      <Header as="h5">
        <Icon name="info circle" />
        <Header.Content>
          {m.action}
          {m.reversible && (
            <Header.Subheader style={{ color: "#21ba45" }}>
              <Icon name="undo" /> Reversible
            </Header.Subheader>
          )}
          {!m.reversible && (
            <Header.Subheader style={{ color: "#db2828" }}>
              <Icon name="warning sign" /> Irreversible
            </Header.Subheader>
          )}
        </Header.Content>
      </Header>
      <Statistic.Group widths="five" size="mini">
        <Statistic>
          <Statistic.Value>{docs.user || 0}</Statistic.Value>
          <Statistic.Label>User</Statistic.Label>
        </Statistic>
        <Statistic>
          <Statistic.Value>{docs.teacher || 0}</Statistic.Value>
          <Statistic.Label>Teacher</Statistic.Label>
        </Statistic>
        <Statistic>
          <Statistic.Value>{docs.student || 0}</Statistic.Value>
          <Statistic.Label>Student</Statistic.Label>
        </Statistic>
        <Statistic>
          <Statistic.Value>{docs.therapists || 0}</Statistic.Value>
          <Statistic.Label>Therapists</Statistic.Label>
        </Statistic>
        <Statistic>
          <Statistic.Value>{docs.patients || 0}</Statistic.Value>
          <Statistic.Label>Patients</Statistic.Label>
        </Statistic>
      </Statistic.Group>
      {typeof m.avatars === "number" && m.avatars > 0 && (
        <p style={{ marginTop: 12, color: "#888" }}>
          <Icon name="image" /> Se borraran {m.avatars} avatar(es) de S3.
        </p>
      )}
      {Array.isArray(m.warnings) && m.warnings.length > 0 && (
        <Message warning>
          <Message.Header>Avisos importantes</Message.Header>
          <Message.List items={m.warnings} />
        </Message>
      )}
    </Segment>
  );
}

/**
 * Modal de eliminacion 3-niveles. Pide preview al backend al abrirse y deja al
 * admin elegir entre los 3 modos. Para los irreversibles exige tipear el email
 * exacto del usuario.
 *
 * El backend tambien valida `confirmEmail` (case-insensitive). El cliente
 * agrega validacion local instant-feedback para UX.
 */
export function UsersV2DeleteModal({ user, open, onClose, onDeleted }) {
  const { accessToken } = useAuth();
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [scope, setScope] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [selectedMode, setSelectedMode] = useState(null);
  const [confirmEmail, setConfirmEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitResult, setSubmitResult] = useState(null);

  useEffect(() => {
    if (!open || !user?._id) {
      setScope(null);
      setSelectedMode(null);
      setConfirmEmail("");
      setSubmitResult(null);
      setSubmitError(null);
      setPreviewError(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoadingPreview(true);
        setPreviewError(null);
        const data = await usersV2Api.deletePreview(accessToken, user._id);
        if (!cancelled) setScope(data.scope);
      } catch (e) {
        if (!cancelled) {
          setPreviewError(e?.message || "Error obteniendo el preview.");
        }
      } finally {
        if (!cancelled) setLoadingPreview(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, user, accessToken]);

  const currentMode = MODES.find((m) => m.key === selectedMode);
  const requiresConfirm = currentMode?.needsConfirm;
  const expectedEmail = String(user?.email || "")
    .trim()
    .toLowerCase();
  const providedEmail = String(confirmEmail || "")
    .trim()
    .toLowerCase();
  const confirmOk = !requiresConfirm || providedEmail === expectedEmail;
  const canSubmit = !!selectedMode && confirmOk && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    try {
      setSubmitting(true);
      setSubmitError(null);
      const result = await usersV2Api.deleteUser(
        accessToken,
        user._id,
        selectedMode,
        requiresConfirm ? confirmEmail : undefined,
      );
      setSubmitResult(result);
      if (onDeleted) onDeleted(result);
    } catch (e) {
      setSubmitError(e?.message || "Error ejecutando el borrado.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} size="large" closeIcon>
      <Modal.Header>
        <Icon name="trash" /> Eliminar usuario
        {user?.email && <span style={{ color: "#666" }}> — {user.email}</span>}
      </Modal.Header>
      <Modal.Content scrolling>
        {loadingPreview && (
          <Loader active inline="centered">
            Calculando alcance del borrado...
          </Loader>
        )}
        {previewError && (
          <Message negative>
            <Message.Header>Error</Message.Header>
            <p>{previewError}</p>
          </Message>
        )}

        {scope && !submitResult && (
          <>
            <Header as="h4">Selecciona el modo</Header>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: 12,
                marginBottom: 16,
              }}
            >
              {MODES.map((m) => {
                const isSelected = selectedMode === m.key;
                return (
                  <Button
                    key={m.key}
                    type="button"
                    fluid
                    color={isSelected ? m.color : undefined}
                    basic={!isSelected}
                    onClick={() => {
                      setSelectedMode(m.key);
                      setConfirmEmail("");
                    }}
                    style={{ height: 64, whiteSpace: "normal" }}
                  >
                    <strong>{m.title}</strong>
                  </Button>
                );
              })}
            </div>

            {currentMode && (
              <>
                <p style={{ color: "#666" }}>{currentMode.description}</p>
                <ScopeSummary scope={scope} modeKey={currentMode.key} />

                {requiresConfirm && (
                  <Segment>
                    <Form>
                      <Form.Field>
                        <label>
                          Para confirmar, escribe el email del usuario:{" "}
                          <code>{user?.email}</code>
                        </label>
                        <Input
                          placeholder={user?.email}
                          value={confirmEmail}
                          onChange={(_, { value }) => setConfirmEmail(value)}
                          error={
                            !!confirmEmail && providedEmail !== expectedEmail
                          }
                          icon={
                            confirmOk && confirmEmail
                              ? "check"
                              : "exclamation triangle"
                          }
                        />
                      </Form.Field>
                    </Form>
                  </Segment>
                )}
              </>
            )}
          </>
        )}

        {submitResult && (
          <Message positive>
            <Message.Header>Operacion completada</Message.Header>
            <Message.List>
              <Message.Item>Modo: {submitResult.mode}</Message.Item>
              {submitResult.deactivated && (
                <Message.Item>Usuario desactivado correctamente.</Message.Item>
              )}
              {submitResult.deleted && (
                <Message.Item>
                  Borrados — User: {submitResult.deleted.user || 0}, Teacher:{" "}
                  {submitResult.deleted.teacher || 0}, Student:{" "}
                  {submitResult.deleted.student || 0}, Therapists:{" "}
                  {submitResult.deleted.therapists || 0}, Patients:{" "}
                  {submitResult.deleted.patients || 0}
                </Message.Item>
              )}
              <Message.Item>
                Duracion: {submitResult.duration_ms || 0} ms
              </Message.Item>
              {Array.isArray(submitResult.errors) &&
                submitResult.errors.length > 0 && (
                  <Message.Item>
                    Errores parciales: {submitResult.errors.length}
                  </Message.Item>
                )}
            </Message.List>
          </Message>
        )}

        {submitError && (
          <Message negative>
            <Message.Header>Error</Message.Header>
            <p>{submitError}</p>
          </Message>
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose}>
          {submitResult ? "Cerrar" : "Cancelar"}
        </Button>
        {!submitResult && (
          <Button
            color={currentMode?.color || "red"}
            disabled={!canSubmit}
            loading={submitting}
            onClick={handleSubmit}
            icon
            labelPosition="right"
          >
            Confirmar
            <Icon name="right chevron" />
          </Button>
        )}
      </Modal.Actions>
    </Modal>
  );
}
